import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table
} from 'reactstrap';
import apiBase from '../../apiConfig';

const USERS_ENDPOINT = `${apiBase}/p-admin/users.php`;

const EMPTY_FORM = {
  username: '',
  full_name: '',
  job_title: '',
  email: '',
  avatar: '',
  role: 'psychologist_operator',
  password: ''
};

const roleBadgeColor = role => {
  if (role === 'platform_admin') {
    return 'soft-danger';
  }

  if (role === 'psychologist_manager') {
    return 'soft-primary';
  }

  return 'soft-info';
};

const statusBadgeColor = archived => (archived ? 'soft-secondary' : 'soft-success');

const SettingsUserManagement = ({ currentUser, onCurrentUserUpdate }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, archived: 0, admins: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const applyPayload = useCallback(payload => {
    setUsers(Array.isArray(payload?.users) ? payload.users : []);
    setRoles(Array.isArray(payload?.roles) ? payload.roles : []);
    setStats(payload?.stats || { total: 0, active: 0, archived: 0, admins: 0 });

    if (payload?.current_user && typeof onCurrentUserUpdate === 'function') {
      onCurrentUserUpdate(payload.current_user);
    }
  }, [onCurrentUserUpdate]);

  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(USERS_ENDPOINT);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Foydalanuvchilarni yuklab bo`lmadi.');
      }

      applyPayload(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Foydalanuvchilar ro`yxati yuklanmadi.');
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const resetForm = roleValue => {
    setEditingUser(null);
    setForm({
      ...EMPTY_FORM,
      role: roleValue || roles[0]?.value || EMPTY_FORM.role
    });
  };

  const toggleModal = () => {
    const nextOpen = !modalOpen;
    setModalOpen(nextOpen);

    if (!nextOpen) {
      resetForm();
    }
  };

  const handleCreate = () => {
    resetForm(roles[0]?.value || EMPTY_FORM.role);
    setModalOpen(true);
  };

  const handleEdit = user => {
    setEditingUser(user);
    setForm({
      username: user.username || '',
      full_name: user.full_name || '',
      job_title: user.job_title || '',
      email: user.email || '',
      avatar: user.avatar || '',
      role: user.role || roles[0]?.value || EMPTY_FORM.role,
      password: ''
    });
    setModalOpen(true);
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!form.username.trim() || !form.full_name.trim() || !form.role) {
      toast.error('Login, F.I.Sh va rol maydonlarini to`ldiring.');
      return;
    }

    if (!editingUser && form.password.length < 8) {
      toast.error('Yangi foydalanuvchi uchun parol kamida 8 ta belgi bo`lishi kerak.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(USERS_ENDPOINT, {
        action: editingUser ? 'update' : 'create',
        id: editingUser?.id,
        ...form
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Saqlashda xatolik yuz berdi.');
      }

      applyPayload(response.data.data);
      toast.success(response.data.message || 'Saqlandi.');
      setModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Saqlashda xatolik yuz berdi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveToggle = async user => {
    const nextArchived = !user.archived;
    const question = nextArchived
      ? `${user.full_name || user.username} akkauntini arxivga yuborilsinmi?`
      : `${user.full_name || user.username} akkauntini qayta faollashtirilsinmi?`;

    if (!window.confirm(question)) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(USERS_ENDPOINT, {
        action: 'archive',
        id: user.id,
        archived: nextArchived
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Holatni yangilab bo`lmadi.');
      }

      applyPayload(response.data.data);
      toast.success(response.data.message || 'Holat yangilandi.');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Holatni yangilab bo`lmadi.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return users.filter(user => {
      if (roleFilter !== 'all' && user.role !== roleFilter) {
        return false;
      }

      if (statusFilter === 'active' && user.archived) {
        return false;
      }

      if (statusFilter === 'archived' && !user.archived) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [user.full_name, user.username, user.email, user.job_title]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [users, search, roleFilter, statusFilter]);

  const summaryCards = [
    { title: 'Jami akkaunt', value: stats.total, note: 'P-admin uchun ro`yxatga olingan foydalanuvchilar' },
    { title: 'Faol', value: stats.active, note: 'Tizimga kirishi mumkin bo`lgan akkauntlar' },
    { title: 'Arxiv', value: stats.archived, note: 'Kirishdan bloklangan, lekin saqlanib qolgan akkauntlar' },
    { title: 'Administrator', value: stats.admins, note: 'To`liq ruxsatga ega hisoblar' }
  ];

  return (
    <>
      <Row>
        {summaryCards.map(card => (
          <Col md={6} xl={3} key={card.title} className="mb-3">
            <div className="med-settings-status-card h-100">
              <span>{card.title}</span>
              <strong>{card.value}</strong>
              <small>{card.note}</small>
            </div>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="med-settings-shell-card">
          <Row className="align-items-end">
            <Col lg={4} className="mb-3">
              <Label className="mb-2">Qidiruv</Label>
              <Input value={search} onChange={({ target }) => setSearch(target.value)} placeholder="F.I.Sh, login yoki email" />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Label className="mb-2">Rol</Label>
              <Input type="select" value={roleFilter} onChange={({ target }) => setRoleFilter(target.value)}>
                <option value="all">Barcha rollar</option>
                {roles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Label className="mb-2">Holat</Label>
              <Input type="select" value={statusFilter} onChange={({ target }) => setStatusFilter(target.value)}>
                <option value="active">Faqat faol</option>
                <option value="archived">Faqat arxiv</option>
                <option value="all">Barchasi</option>
              </Input>
            </Col>
            <Col lg={2} className="mb-3">
              <Button color="primary" block onClick={handleCreate} disabled={loading || submitting}>
                <FontAwesomeIcon icon="user-plus" className="mr-2" />
                Qo`shish
              </Button>
            </Col>
          </Row>
          <div className="med-settings-preview-note mb-0">
            <div className="med-settings-preview-note__icon">
              <FontAwesomeIcon icon="users" />
            </div>
            <div>
              <strong>Arxivlangan akkauntlar kirishdan bloklanadi</strong>
              <span>Holat o`zgarganda remember token va session izlari tozalanadi.</span>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardBody className="med-settings-shell-card">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="mb-1">Foydalanuvchilar ro`yxati</h5>
              <p className="text-500 mb-0">{filteredUsers.length} ta yozuv ko`rinmoqda.</p>
            </div>
            {submitting && !loading ? <Spinner size="sm" color="primary" /> : null}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-5 text-600">Tanlangan filtrlar bo`yicha foydalanuvchi topilmadi.</div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-200">
                  <tr>
                    <th>Foydalanuvchi</th>
                    <th>Rol</th>
                    <th>Lavozim</th>
                    <th>Email</th>
                    <th>Holat</th>
                    <th className="text-right">Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const isCurrentUser = currentUser?.id === user.id;

                    return (
                      <tr key={user.id}>
                        <td className="align-middle">
                          <div className="d-flex align-items-center">
                            <div className="avatar avatar-xl mr-3">
                              <div className="avatar-name rounded-circle bg-soft-primary text-primary">
                                <span>{(user.full_name || user.username || '?').slice(0, 1).toUpperCase()}</span>
                              </div>
                            </div>
                            <div>
                              <div className="font-weight-semi-bold">{user.full_name || '-'}</div>
                              <div className="text-500 fs--1">{user.username}</div>
                            </div>
                          </div>
                        </td>
                        <td className="align-middle">
                          <Badge color={roleBadgeColor(user.role)} pill>
                            {user.role_label}
                          </Badge>
                        </td>
                        <td className="align-middle">{user.job_title || '-'}</td>
                        <td className="align-middle">{user.email || '-'}</td>
                        <td className="align-middle">
                          <Badge color={statusBadgeColor(user.archived)} pill>
                            {user.archived ? 'Arxiv' : 'Faol'}
                          </Badge>
                          {isCurrentUser ? (
                            <Badge color="soft-info" pill className="ml-2">
                              Joriy
                            </Badge>
                          ) : null}
                        </td>
                        <td className="align-middle text-right text-nowrap">
                          <Button color="link" className="text-primary p-0 mr-3" onClick={() => handleEdit(user)}>
                            <FontAwesomeIcon icon="edit" className="mr-1" />
                            Tahrirlash
                          </Button>
                          <Button color="link" className="text-warning p-0" onClick={() => handleArchiveToggle(user)} disabled={isCurrentUser}>
                            <FontAwesomeIcon icon={user.archived ? 'redo' : 'archive'} className="mr-1" />
                            {user.archived ? 'Tiklash' : 'Arxivlash'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>{editingUser ? 'Foydalanuvchini tahrirlash' : 'Yangi foydalanuvchi qo`shish'}</ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Login</Label>
                  <Input value={form.username} onChange={({ target }) => setForm(current => ({ ...current, username: target.value }))} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Rol</Label>
                  <Input type="select" value={form.role} onChange={({ target }) => setForm(current => ({ ...current, role: target.value }))}>
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>F.I.Sh</Label>
                  <Input value={form.full_name} onChange={({ target }) => setForm(current => ({ ...current, full_name: target.value }))} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Lavozim</Label>
                  <Input value={form.job_title} onChange={({ target }) => setForm(current => ({ ...current, job_title: target.value }))} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={({ target }) => setForm(current => ({ ...current, email: target.value }))} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Avatar kaliti</Label>
                  <Input value={form.avatar} onChange={({ target }) => setForm(current => ({ ...current, avatar: target.value }))} />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup className="mb-0">
                  <Label>Parol</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={({ target }) => setForm(current => ({ ...current, password: target.value }))}
                    placeholder={editingUser ? 'Bo`sh qoldirilsa o`zgarmaydi' : 'Kamida 8 ta belgi'}
                  />
                </FormGroup>
                <small className="text-500 d-block mt-2">
                  {editingUser
                    ? 'Parol maydoni to`ldirilsa yangi parol saqlanadi va eski tokenlar bekor qilinadi.'
                    : 'Yangi foydalanuvchi uchun vaqtinchalik yoki doimiy parol kiriting.'}
                </small>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal} disabled={submitting}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="check-circle" className="mr-2" />}
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

SettingsUserManagement.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.number
  }),
  onCurrentUserUpdate: PropTypes.func
};

SettingsUserManagement.defaultProps = {
  currentUser: null,
  onCurrentUserUpdate: null
};

export default SettingsUserManagement;
